/**
 * JWTs anywhere in a text, in linear time (security-slice reviews SBT-06 and round 3, Fable F4).
 *
 * `header.payload.signature`, header and payload each starting `eyJ` (base64url of `{"`), signature possibly empty
 * (an unsigned token). The token may follow ANY character — `%20eyJ` (a URL-encoded header), `"eyJ` (.NET's escaped
 * quote), a literal `\neyJ`, `_eyJ`, `xeyJ` — which the anchored regex that made the match linear had stopped catching.
 * Linear without the anchor: a candidate that fails skips the whole run of token characters it read, because every
 * later start inside that run ends at the same place and fails the same way.
 */
const isTokenChar = (code: number): boolean =>
  (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122) || code === 45 || code === 95;

function runEnd(text: string, from: number): number {
  let i = from;
  while (i < text.length && isTokenChar(text.charCodeAt(i))) i += 1;
  return i;
}

/** The [start, end) spans of every JWT in the text. */
export function jwtSpans(text: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  let from = 0;
  for (let start = text.indexOf('eyJ', from); start >= 0; start = text.indexOf('eyJ', from)) {
    const headerEnd = runEnd(text, start + 3);
    if (headerEnd === start + 3 || text[headerEnd] !== '.' || !text.startsWith('eyJ', headerEnd + 1)) {
      from = Math.max(headerEnd, start + 1);
      continue;
    }
    const payloadEnd = runEnd(text, headerEnd + 4);
    if (payloadEnd === headerEnd + 4 || text[payloadEnd] !== '.') {
      from = Math.max(payloadEnd, start + 1);
      continue;
    }
    const end = runEnd(text, payloadEnd + 1);
    spans.push([start, end]);
    // Two tokens with no separator (review round 4, Fable N6): the signature run swallowed the next token's header, so
    // look again from that `eyJ` — the whole run stays masked, and the next token's payload and signature are found.
    const next = text.indexOf('eyJ', payloadEnd + 1);
    from = next > payloadEnd && next < end && text[end] === '.' && text.startsWith('eyJ', end + 1) ? next : end;
  }
  return spans;
}

/** The text with every JWT replaced. */
export function replaceJwts(text: string, replacement: string): string {
  const spans = jwtSpans(text);
  if (spans.length === 0) return text;
  const parts: string[] = [];
  let from = 0;
  for (const [start, end] of spans) {
    if (end <= from) continue;
    parts.push(text.slice(from, Math.max(start, from)), replacement);
    from = end;
  }
  parts.push(text.slice(from));
  return parts.join('');
}
